import type { CheckpointCrossing, CheckpointTraceResult, Point } from './StrokeEvaluator';

type ReplayCheckpoint = Pick<CheckpointCrossing, 'point' | 'checkpointIndex' | 'score' | 'distance'>;

type StrokeAttemptConfig = {
	start: Point;
	maxInk: number;
	inputDeadzone: number;
	evaluateSegment: (
		from: Point,
		to: Point,
		maxMovementProgress: number,
		endpointOnly?: boolean,
	) => CheckpointTraceResult;
};

export type StrokeAttemptResult = CheckpointTraceResult & {
	position: Point;
	outOfInk: boolean;
};

export class StrokeAttempt {
	private inkUsed = 0;
	private started = false;
	private readonly crossings: ReplayCheckpoint[] = [];
	private readonly points: [Point, ...Point[]];

	constructor(private readonly config: StrokeAttemptConfig) {
		this.points = [{ ...config.start }];
	}

	startAt(point: Point) {
		if (this.started) {
			return false;
		}
		this.started = true;
		this.points[0] = { ...point };
		return true;
	}

	getPoints(): readonly [Point, ...Point[]] {
		return this.points;
	}

	getCrossings(): readonly ReplayCheckpoint[] {
		return this.crossings;
	}

	trace(from: Point, to: Point): StrokeAttemptResult | null {
		const movementLength = Math.hypot(to.x - from.x, to.y - from.y);

		const remainingInk = Math.max(0, this.config.maxInk - this.inkUsed);
		const acceptedProgress = movementLength === 0 ? 0 : Math.min(1, remainingInk / movementLength);
		const endpointOnly = movementLength < this.config.inputDeadzone;
		const result = this.config.evaluateSegment(from, to, acceptedProgress, endpointOnly);
		if (endpointOnly && !result.completed) return null;
		this.started = true;
		const renderedProgress = result.completion?.movementProgress ?? acceptedProgress;
		const acceptedPosition = result.completion?.point ?? {
			x: from.x + (to.x - from.x) * renderedProgress,
			y: from.y + (to.y - from.y) * renderedProgress,
		};
		const position = result.completion?.endpoint ?? acceptedPosition;

		result.crossings.forEach(({ point, movementProgress, checkpointIndex, score }) => {
			this.crossings.push({
				point,
				checkpointIndex,
				score,
				distance: this.inkUsed + movementLength * movementProgress,
			});
		});
		this.inkUsed += movementLength * renderedProgress;
		if (renderedProgress > 0) this.points.push(acceptedPosition);
		if (position.x !== acceptedPosition.x || position.y !== acceptedPosition.y) this.points.push({ ...position });

		return { ...result, position, outOfInk: !result.completed && movementLength >= remainingInk };
	}
}
